// Eating (PLR-4; M11.2). PURE: the hunger/saturation restoration model, matching
// Minecraft — hunger points add directly (clamped to 20), saturation gains
// `nutrition × saturationModifier × 2`, never exceeding the new hunger. Food is
// eaten by holding use for EAT_TICKS; the host drives the timer.
import { SURVIVAL } from '../config';

/** A status effect a food grants when eaten (MB.3). */
export interface FoodEffect {
  effect: string;
  amplifier: number;
  /** Duration in seconds; 0 ⇒ instant effect. */
  seconds: number;
  /** Probability the effect is granted (suspicious stew rolls; most are 1). */
  chance: number;
}

export interface FoodDef {
  nutrition: number;
  saturation: number;
  alwaysEdible: boolean;
  effects?: readonly FoodEffect[];
}

/** An effect grant resolved from a food (host applies via addEffect/applyInstant). */
export interface GrantedEffect { id: string; amplifier: number; ticks: number; }

/**
 * Resolve which effects a food actually grants this bite. `roll(i)` supplies a
 * 0..1 value per effect for the chance gate (deterministic in the host). Seconds
 * convert to game ticks (×20). PURE.
 */
export function foodEffects(food: FoodDef, roll: (index: number) => number): GrantedEffect[] {
  if (!food.effects) return [];
  const out: GrantedEffect[] = [];
  food.effects.forEach((fe, i) => {
    if (roll(i) >= fe.chance) return;
    out.push({ id: fe.effect, amplifier: fe.amplifier, ticks: Math.round(fe.seconds * 20) });
  });
  return out;
}

export interface Eater {
  hunger: number;
  saturation: number;
}

/** Hold-to-eat duration (Minecraft 32 game ticks = 1.6 s). */
export const EAT_TICKS = 32;

/** Can this food be eaten now? Only when hungry, unless it's always edible. */
export function canEat(hunger: number, food: FoodDef): boolean {
  return food.alwaysEdible || hunger < SURVIVAL.MAX_HUNGER;
}

/** Apply a consumed food's nutrition + saturation to the eater (clamped). */
export function applyFood(e: Eater, food: FoodDef): void {
  e.hunger = Math.min(SURVIVAL.MAX_HUNGER, e.hunger + food.nutrition);
  e.saturation = Math.min(e.hunger, e.saturation + food.nutrition * food.saturation * 2);
}
